import { useTranslation } from "react-i18next"
import styled from "styled-components"
import ChargerType from "../types/ChargerType"
import Modal from "../components/Modal"
import Icon from "../Icon"
import secondsToHumanReadable from "../utils/secondsToHumanReadable"
import { BREAKPOINTS, TAP_MIN } from "../GlobalTheme"

const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

const DetailsHintSpan = styled.span`
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.colors.gray_900};

  /* Negative margin keeps the desktop row height unchanged while the hit area grows. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: ${TAP_MIN}px;
    min-height: ${TAP_MIN}px;
    margin: -13px;
  }
`

// Small "?" trigger to place in a charger row/cell; opens the details modal when clicked
export function ChargerDetailsHint({ onClick, title }: { onClick: () => void, title?: string }) {
  // Stop propagation so placing this inside a clickable charger row/link doesn't also trigger that
  return <DetailsHintSpan onClick={(e) => { e.stopPropagation(); onClick() }} title={title}>?</DetailsHintSpan>
}

// Reusable on-demand modal showing every field of a charger (from the /chargers response)
export default function ChargerDetailsModal({ charger, onClose }: { charger: ChargerType | null, onClose: () => void }) {
  const { t } = useTranslation()

  if (!charger) return null

  const detailFields: { label: string, value: string | number }[] = [
    { label: t('chargers_chargerId'), value: charger.chargerId },
    { label: t('charger_serialNumber'), value: charger.serialNumber && charger.serialNumber !== ' ' ? charger.serialNumber : '-' },
    { label: t('chargers_userIdUserEmail'), value: `${charger.userEmail || '-'} / ${charger.userId || '-'}` },
    { label: t('chargers_registrationDate'), value: charger.registrationDate || '-' },
    { label: t('chargers_lastOnline'), value: charger.lastPresence || '-' },
    { label: t('chargers_swVersion'), value: charger.swVersion || '-' },
    { label: t('chargers_fwVersion'), value: charger.fwVersion || '-' },
    { label: t('chargers_osVersion'), value: charger.osVersion || '-' },
    { label: t('charger_mcuVersion'), value: charger.mcuVersion ?? '-' },
    { label: t('charger_hpowVersion'), value: charger.hpowVersion ?? '-' },
    { label: t('charger_dailyLogVersion'), value: charger.dailyLogVersion ?? '-' },
    { label: t('charger_platform'), value: charger.platform || '-' },
    { label: t('charger_storage'), value: charger.storage ? `${charger.storage}${charger.storageSizeGb ? ` ${charger.storageSizeGb} GB` : ''}` : '-' },
    { label: t('charger_uptime'), value: charger.uptime ? `${Math.floor(charger.uptime / 86400)}d ${Math.floor((charger.uptime % 86400) / 3600)}h` : '-' },
    { label: t('charger_event'), value: charger.event || '-' },
    { label: t('charger_accessMode'), value: charger.accessMode ?? '-' },
    { label: t('charger_powerSharing'), value: charger.psEnabled ? t('charger_yes') : t('charger_no') },
    { label: t('charger_outOfService'), value: charger.outOfService ? t('charger_yes') : t('charger_no') },
    { label: t('charger_midMeter'), value: charger.midMeterEn ? (charger.midMeter || '-') : t('charger_disabled') },
    { label: t('charger_midMeterEnergy'), value: charger.midMeterEn && charger.midKwh != null && charger.midKwh >= 0 ? `${charger.midKwh} kWh` : '-' },
    { label: t('charger_dlmMode'), value: charger.dlmMode || '-' },
    { label: t('charger_dlmMeter'), value: charger.dlmMeter || '-' },
    { label: t('charger_ethernetMode'), value: charger.ethMode || '-' },
    { label: t('charger_ethernetIp'), value: charger.ethIp || '-' },
    { label: t('charger_wifiMode'), value: charger.wifiMode || '-' },
    { label: t('charger_wifiSsid'), value: charger.wifiSsid || '-' },
    { label: t('charger_apModeSsid'), value: charger.apModeSsid || '-' },
    { label: t('charger_wifiIp'), value: charger.wifiIp || '-' },
    { label: t('charger_gsmMode'), value: charger.gsmMode || '-' },
    { label: t('charger_gsmModem'), value: charger.gsmModem || '-' },
    { label: t('charger_gsmApn'), value: charger.gsmApn || '-' },
    { label: t('charger_gsmNetwork'), value: charger.gsmNetwork || '-' },
    { label: t('charger_gsmIccid'), value: charger.gsmIccid || '-' },
    { label: t('charger_allChargedEnergy'), value: charger.lifetimeWh != null ? `${charger.lifetimeWh} Wh` : '-' },
    { label: t('charger_allChargetime'), value: charger.lifetimeChgSec != null ? secondsToHumanReadable(charger.lifetimeChgSec) : '-' },
    { label: t('charger_relaySwitches'), value: charger.lifetimeSwitch ?? '-' },
  ]

  return (
    <Modal onEscape={onClose}>
      <div style={{ padding: '24px', width: 'min(max(40vw, 512px), calc(100vw - 72px))', position: 'relative' }}>
        <ModalCloseButtonContainer>
          <Icon path="general.x" width="20px" height="20px" onClick={onClose} />
        </ModalCloseButtonContainer>
        <p style={{ fontWeight: 700, fontSize: '16px', margin: 0 }}>{t('chargers_details')}</p>
        <p style={{ fontWeight: 600, fontSize: '13px', color: '#A1A5B7', margin: 0, marginBottom: '12px' }}>{charger.localName} ({charger.chargerId})</p>
        <div style={{ maxHeight: '70dvh', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {detailFields.map(field => (
                <tr key={field.label} style={{ borderBottom: '1px solid #F1F1F4' }}>
                  <td style={{ padding: '6px 8px 6px 0', fontSize: '12px', fontWeight: 600, color: '#5E6278', width: '45%', verticalAlign: 'top' }}>{field.label}</td>
                  <td style={{ padding: '6px 0', fontSize: '13px', fontWeight: 600, color: '#181C32', wordBreak: 'break-all' }}>{field.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  )
}
